import {Alert, Button, DatePicker, Modal, Space, Table, Tag, message} from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import type {ColumnsType} from "antd/es/table";
import moment from "moment";
import type {ReactNode} from "react";
import {useEffect, useState} from "react";
import {StyledPageHeader} from "../../../../../styles/dashboard/calls/calls";
import {
  StyledNoUpcomingCallBox,
  StyledUpcomingCallsWrapper,
} from "../../../../calls/upcoming-calls/upcoming-calls.style";
import {FullPageLoader} from "../../../../full-page-loader";
import {useGetSubscriptionSubscribersApi} from "../../../../../react-query-hooks/store/subscriptions/get-subscription-subscribers";
import type {SubscriptionSubscriberListItem} from "../../../../../react-query-hooks/store/subscriptions/get-subscription-subscribers/types";
import {CustomPagination} from "../../../../pagination";
import {StyledDescriptionText} from "../../../../messages/messages.style";
import {useCancelSubscriptionSubscriberApi} from "../../../../../react-query-hooks/store/subscriptions/cancel-subscription-subscriber";
import {useGetSubscriptionSubscriberApi} from "../../../../../react-query-hooks/store/subscriptions/get-subscription-subscriber";
import type {
  GetSubscriptionSubscriberApiResponse,
  SubscriptionSubscriberRecentBilling,
} from "../../../../../react-query-hooks/store/subscriptions/get-subscription-subscriber";
import {StyledSessionDetailsDrawer} from "../../../../calls/service/sales-and-insights/customers-section/style";
import {
  StyledSessionDetailsDrawerBookingDetailsHeader,
  StyledSessionDetailsDrawerBookingQuestionsItem,
} from "../../../../calls/service/sales-and-insights/customers-section/booking-questions/style";
import {
  StyledSessionDetailsDrawerBookingDetailsItem,
  StyledSessionDetailsDrawerBookingDetailsWrapper,
} from "../../../../calls/service/sales-and-insights/customers-section/booking-details/style";
import {subscriptionBillingLabel} from "../../../../../react-query-hooks/store/subscriptions/constants/billing-enums";
import {
  type ExportSubscriptionCustomerPurchasesCsvParams,
  useExportSubscriptionCustomerPurchasesCSV,
} from "../../../../../react-query-hooks/store/subscriptions/export-subscription-customer-purchases-csv";
import {formatCurrencyIsoCodeAndAmount} from "../../../../../utils/format-currency-amount";

const {RangePicker} = DatePicker;

function monthToDateExportRange(): [moment.Moment, moment.Moment] {
  return [moment().startOf("month"), moment().endOf("day")];
}

type SubscriptionCustomerInsightsProps = {
  subscriptionId: number;
  subscriptionTitle: string;
};

type DetailItem = {
  title: string;
  value: ReactNode;
};

function DetailsCard({items}: {items: DetailItem[]}) {
  return (
    <StyledSessionDetailsDrawerBookingDetailsWrapper>
      {items.map((item) => (
        <StyledSessionDetailsDrawerBookingDetailsItem key={item.title}>
          <p className="item-title">{item.title}</p>
          <div className="item-value">{item.value}</div>
        </StyledSessionDetailsDrawerBookingDetailsItem>
      ))}
    </StyledSessionDetailsDrawerBookingDetailsWrapper>
  );
}

function formatDate(date?: string) {
  return date ? moment(date).format("MMMM D, YYYY") : "-";
}

function formatMoney(currency: string, amount: number) {
  return formatCurrencyIsoCodeAndAmount(amount, currency);
}

function formatSubscriptionDetailPrice(
  d: GetSubscriptionSubscriberApiResponse["subscriptionDetails"]
) {
  if (typeof d.amount === "number" && Number.isFinite(d.amount)) {
    return formatMoney(d.currency, d.amount);
  }
  const n = Number(d.price);
  if (Number.isFinite(n)) {
    return formatMoney(d.currency, n);
  }
  return d.price;
}

function statusTag(statusName?: string) {
  const normalizedStatus = statusName?.toLowerCase();
  const color =
    normalizedStatus === "active" || normalizedStatus === "paid" ? "green" : "default";

  return <Tag color={color}>{statusName ?? "-"}</Tag>;
}

export function SubscriptionCustomerInsights({
  subscriptionId,
  subscriptionTitle,
}: SubscriptionCustomerInsightsProps) {
  const [page, setPage] = useState(1);
  const [selectedSubscriberId, setSelectedSubscriberId] = useState<number | undefined>();
  const [csvExportDateRange, setCsvExportDateRange] =
    useState<[moment.Moment, moment.Moment]>(monthToDateExportRange);
  const getSubscriptionSubscribersQuery = useGetSubscriptionSubscribersApi({
    subscriptionId,
    currentPage: page,
  });
  const getSubscriptionSubscriberQuery = useGetSubscriptionSubscriberApi({
    subscriptionId,
    subscriberId: selectedSubscriberId,
  });
  const cancelSubscriptionSubscriberMutation = useCancelSubscriptionSubscriberApi();
  const exportSubscribersCsvMutation = useExportSubscriptionCustomerPurchasesCSV();

  function closeSubscriberDetailsDrawer() {
    setSelectedSubscriberId(undefined);
  }

  useEffect(() => {
    setCsvExportDateRange(monthToDateExportRange());
  }, [subscriptionId]);

  if (getSubscriptionSubscribersQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (getSubscriptionSubscribersQuery.isError) {
    return (
      <Alert
        message="Error"
        description={getSubscriptionSubscribersQuery.error.message}
        type="error"
        showIcon
      />
    );
  }

  const subscribers = getSubscriptionSubscribersQuery.data?.subscribers ?? [];
  const subscriberPaginationMeta = getSubscriptionSubscribersQuery.data
    ? {
        current_page: getSubscriptionSubscribersQuery.data.page,
        next_page: getSubscriptionSubscribersQuery.data.hasNextPage
          ? getSubscriptionSubscribersQuery.data.page + 1
          : 0,
        per_page: getSubscriptionSubscribersQuery.data.perPage,
        previous_page: getSubscriptionSubscribersQuery.data.hasPrevPage
          ? getSubscriptionSubscribersQuery.data.page - 1
          : 0,
        total_pages: getSubscriptionSubscribersQuery.data.totalPages,
      }
    : undefined;

  const columns: ColumnsType<SubscriptionSubscriberListItem> = [
    {
      title: "Subscribers Name",
      key: "name",
      fixed: "left",
      width: "17.425rem",
      render: (_, record) => (
        <StyledDescriptionText style={{textTransform: "capitalize"}}>
          {`${record.customerFirstName} ${record.customerLastName}`}
        </StyledDescriptionText>
      ),
    },
    {
      title: "Subscribers Email",
      dataIndex: "customerEmail",
      key: "customerEmail",
      width: "17.425rem",
      render: (email) => <StyledDescriptionText>{email}</StyledDescriptionText>,
    },
    {
      title: "Tier Name",
      dataIndex: "tierName",
      key: "tierName",
      width: "12rem",
      render: (tierName) => <StyledDescriptionText>{tierName}</StyledDescriptionText>,
    },
    {
      title: "Price",
      key: "price",
      width: "10rem",
      render: (_, record) => (
        <StyledDescriptionText>
          {Number.isFinite(record.amount)
            ? formatMoney(record.currency, record.amount)
            : record.price || "—"}
        </StyledDescriptionText>
      ),
    },
    {
      title: "Status",
      dataIndex: "statusName",
      key: "statusName",
      width: "10rem",
      render: (statusName) => (
        <Tag color={statusName?.toLowerCase() === "active" ? "green" : "default"}>
          {statusName}
        </Tag>
      ),
    },
    {
      title: "Next billing",
      dataIndex: "currentPeriodEnd",
      key: "currentPeriodEnd",
      width: "12rem",
      render: (currentPeriodEnd) => (
        <StyledDescriptionText>
          {moment(currentPeriodEnd).format("MMM D, YYYY")}
        </StyledDescriptionText>
      ),
    },
    {
      title: "",
      key: "cancel",
      width: "12rem",
      render: (_, record) => (
        <Button
          disabled={record.statusName?.toLowerCase() !== "active"}
          loading={cancelSubscriptionSubscriberMutation.isLoading}
          onClick={(e) => {
            e.stopPropagation();
            Modal.confirm({
              title: "Confirm subscription cancellation",
              content: `Are you sure you want to cancel ${record.customerFirstName} ${record.customerLastName}'s subscription? This action cannot be undone.`,
              icon: <ExclamationCircleOutlined style={{color: "#F5222D"}} />,
              okText: "Cancel subscription",
              cancelText: "Exit",
              okButtonProps: {
                size: "small",
                type: "ghost",
                danger: true,
                loading: cancelSubscriptionSubscriberMutation.isLoading,
                style: {
                  backgroundColor: "transparent",
                  borderColor: "#F5222D",
                  color: "#F5222D",
                },
              },
              cancelButtonProps: {
                size: "small",
                type: "ghost",
              },
              onOk: () =>
                cancelSubscriptionSubscriberMutation
                  .mutateAsync({
                    customerSubscriptionId: record.id,
                    subscriptionId,
                  })
                  .catch(() => undefined),
            });
          }}
          size="small"
          type="text"
          style={{
            color: "#1c1726",
            padding: 0,
            height: "auto",
            fontWeight: 400,
            border: "none",
            boxShadow: "none",
          }}
        >
          <span
            style={{
              fontWeight: 400,
              borderBottom: "1px solid #1c1726",
              lineHeight: 1.35,
              display: "inline-block",
            }}
          >
            Cancel
          </span>
        </Button>
      ),
    },
  ];

  const recentBillingColumns: ColumnsType<SubscriptionSubscriberRecentBilling> = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <StyledDescriptionText>{formatDate(createdAt)}</StyledDescriptionText>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, record) => (
        <StyledDescriptionText>
          {formatMoney(record.currency, record.amount)}
        </StyledDescriptionText>
      ),
    },
    {
      title: "Status",
      dataIndex: "statusName",
      key: "statusName",
      render: (statusName) => statusTag(statusName),
    },
  ];

  function handleExportSubscribersCsv() {
    const [startM, endM] = csvExportDateRange;
    const payload: ExportSubscriptionCustomerPurchasesCsvParams = {
      subscriptionId,
      startDate: startM.format("YYYY-MM-DD"),
      endDate: endM.format("YYYY-MM-DD"),
    };

    exportSubscribersCsvMutation.mutate(payload, {
      onSuccess: (blob) => {
        message.success("File ready to be downloaded!");
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `subscription-${subscriptionId}-subscribers-${payload.startDate}_${payload.endDate}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
    });
  }

  function renderSubscriberDetails(data: GetSubscriptionSubscriberApiResponse) {
    return (
      <Space direction="vertical" style={{display: "flex"}} size={24}>
        <DetailsCard
          items={[
            {
              title: "Customer's name",
              value: data.customerDetails.name,
            },
            {
              title: "Customer's email",
              value: data.customerDetails.email,
            },
            {
              title: "Date",
              value: formatDate(data.customerDetails.date),
            },
            {
              title: "Reference",
              value: data.customerDetails.paymentReference,
            },
          ]}
        />
        <DetailsCard
          items={[
            {
              title: "Subscription name",
              value: data.subscriptionDetails.subscriptionName,
            },
            {
              title: "Tier name",
              value: data.subscriptionDetails.tierName,
            },
            {
              title: "Price",
              value: formatSubscriptionDetailPrice(data.subscriptionDetails),
            },
            {
              title: "Status",
              value: statusTag(data.statusName),
            },
            {
              title: "Billing frequency",
              value: subscriptionBillingLabel(
                data.subscriptionDetails.billingInterval,
                data.subscriptionDetails.billingDuration
              ),
            },
            {
              title: "No of subscriptions left",
              value: data.subscriptionDetails.billingCyclesLeft ?? "-",
            },
            {
              title: "Start date",
              value: formatDate(data.subscriptionDetails.startDate),
            },
            {
              title: "Next billing date",
              value: formatDate(data.subscriptionDetails.nextBillingDate),
            },
            {
              title: "Total spend",
              value: formatMoney(
                data.subscriptionDetails.currency,
                data.subscriptionDetails.totalSpend
              ),
            },
          ]}
        />
        <div>
          <StyledSessionDetailsDrawerBookingDetailsHeader>
            Recent billing
          </StyledSessionDetailsDrawerBookingDetailsHeader>
          <Table
            columns={recentBillingColumns}
            dataSource={data.recentBillings}
            pagination={false}
            rowKey="id"
            size="small"
          />
        </div>
        {data.checkoutFormResponses && data.checkoutFormResponses.length > 0 ? (
          <Space size={8} direction="vertical" style={{display: "flex"}}>
            <StyledSessionDetailsDrawerBookingDetailsHeader>
              Checkout form responses
            </StyledSessionDetailsDrawerBookingDetailsHeader>
            <Space size={24} direction="vertical" style={{display: "flex"}}>
              {data.checkoutFormResponses.map((response, index) => (
                <StyledSessionDetailsDrawerBookingQuestionsItem
                  key={response.question + index}
                >
                  <p className="bold-text">{response.question}</p>
                  <p>
                    <span className="bold-text">Answer: </span>
                    <span>{response.answer ?? "No answer"}</span>
                  </p>
                </StyledSessionDetailsDrawerBookingQuestionsItem>
              ))}
            </Space>
          </Space>
        ) : null}
      </Space>
    );
  }

  return (
    <>
      <div style={{marginTop: "7rem"}}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "4rem",
          }}
        >
          <StyledPageHeader>Subscribers</StyledPageHeader>
          <Space size={12} wrap style={{justifyContent: "flex-end"}}>
            <RangePicker
              value={csvExportDateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) {
                  setCsvExportDateRange([dates[0], dates[1]]);
                  return;
                }
                setCsvExportDateRange(monthToDateExportRange());
              }}
              format="MMM D, YYYY"
            />
            <Button
              loading={exportSubscribersCsvMutation.isPending}
              icon={<DownloadOutlined style={{fontSize: "1.6rem"}} />}
              type="text"
              size="small"
              title={`Export subscriber purchases for ${subscriptionTitle} within the selected date range`}
              onClick={handleExportSubscribersCsv}
            >
              <span style={{textDecoration: "underline"}}>Export CSV</span>
            </Button>
          </Space>
        </div>
        {subscribers.length < 1 ? (
          <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
            <StyledNoUpcomingCallBox>
              <p className="info-text">Your subscribers will appear here</p>
            </StyledNoUpcomingCallBox>
          </StyledUpcomingCallsWrapper>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={subscribers}
              loading={getSubscriptionSubscribersQuery.isFetching}
              onRow={(record) => ({
                onClick: () => setSelectedSubscriberId(record.id),
              })}
              rowKey="id"
              scroll={{x: 100}}
              pagination={false}
            />
            <CustomPagination
              isPreviousData={getSubscriptionSubscribersQuery.isPreviousData}
              page={page}
              paginationMeta={subscriberPaginationMeta}
              setPage={setPage}
            />
          </>
        )}
      </div>
      <StyledSessionDetailsDrawer
        title="Customer details"
        placement="right"
        onClose={closeSubscriberDetailsDrawer}
        visible={!!selectedSubscriberId}
        closable={false}
        extra={
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={closeSubscriberDetailsDrawer}
          />
        }
      >
        <Space direction="vertical" style={{display: "flex"}} size={16}>
          {getSubscriptionSubscriberQuery.isLoading ? <FullPageLoader /> : null}
          {getSubscriptionSubscriberQuery.isError ? (
            <Alert
              message="Error"
              description={getSubscriptionSubscriberQuery.error.message}
              type="error"
              showIcon
            />
          ) : null}
          {!getSubscriptionSubscriberQuery.isLoading &&
          !getSubscriptionSubscriberQuery.isError &&
          getSubscriptionSubscriberQuery.data
            ? renderSubscriberDetails(getSubscriptionSubscriberQuery.data)
            : null}
        </Space>
      </StyledSessionDetailsDrawer>
    </>
  );
}
